import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const PDF_URL = '/Guia_Usabilidade_Lurik_Interativo.pdf'
const SUMARIO_PAGE = 5 // Página do Sumário no PDF

export const HELP_SCROLL_STORAGE_KEY = 'helpScrollToPage'
export const HELP_SCROLL_EVENT = 'help-scroll-to-page'

const performScrollToPage = (
  pageNum: number,
  numPages: number,
  refs: React.MutableRefObject<(HTMLDivElement | null)[]>
) => {
  const n = Math.max(1, Math.min(numPages, pageNum))
  const el = refs.current[n - 1]
  if (el) {
    // Pequeno delay para garantir que o DOM da página do PDF esteja pronto
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 400)
  }
}

export const HelpSection = () => {
  const theme = useTheme()
  const [numPages, setNumPages] = useState<number>(0)
  const [showBackToSummary, setShowBackToSummary] = useState(false)
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n)
  }, [])

  // Scroll para página específica quando vindo do chat (bot sugeriu Manual com pdf_page)
  useEffect(() => {
    if (numPages === 0) return
    try {
      const pageStr = sessionStorage.getItem(HELP_SCROLL_STORAGE_KEY)
      if (!pageStr) return
      const pageNum = parseInt(pageStr, 10)
      if (Number.isNaN(pageNum)) return
      sessionStorage.removeItem(HELP_SCROLL_STORAGE_KEY)
      performScrollToPage(pageNum, numPages, pageRefs)
    } catch {
      sessionStorage.removeItem(HELP_SCROLL_STORAGE_KEY)
    }
  }, [numPages])

  // Quando o usuário já está na aba Ajuda e clica no chip do chat, o componente não remonta;
  // o evento força a leitura do sessionStorage e o scroll.
  useEffect(() => {
    const handler = () => {
      try {
        const pageStr = sessionStorage.getItem(HELP_SCROLL_STORAGE_KEY)
        if (!pageStr || numPages === 0) return
        const pageNum = parseInt(pageStr, 10)
        if (Number.isNaN(pageNum)) return
        sessionStorage.removeItem(HELP_SCROLL_STORAGE_KEY)
        performScrollToPage(pageNum, numPages, pageRefs)
      } catch {
        sessionStorage.removeItem(HELP_SCROLL_STORAGE_KEY)
      }
    }
    window.addEventListener(HELP_SCROLL_EVENT, handler)
    return () => window.removeEventListener(HELP_SCROLL_EVENT, handler)
  }, [numPages])

  const handleScrollContainer = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return
    // Mostra o botão quando scrollou mais de 600px (após as primeiras páginas)
    setShowBackToSummary(container.scrollTop > 600)
  }, [])

  const handleBackToSummary = useCallback(() => {
    performScrollToPage(SUMARIO_PAGE, numPages, pageRefs)
  }, [numPages])

  const blockContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
        Ajuda
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Consulte o Guia de Usabilidade Lurik para instruções detalhadas. O documento é apenas para
        visualização na plataforma (download desabilitado).
      </Typography>

      <Accordion
        defaultExpanded={true}
        sx={{
          '&:before': { display: 'none' },
          boxShadow: 2,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="help-pdf-content"
          id="help-pdf-header"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&.Mui-expanded': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
          }}
        >
          <Typography variant="h6" fontWeight="600">
            Guia de Usabilidade Lurik Interativo
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, overflow: 'hidden' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 2, pt: 1, pb: 0.5 }}
          >
            Apenas visualização. O PDF é exibido como imagem (download não disponível).
          </Typography>
          <Box
            ref={scrollContainerRef}
            onContextMenu={blockContextMenu}
            onScroll={handleScrollContainer}
            sx={{
              overflow: 'auto',
              maxHeight: 'calc(100vh - 320px)',
              minHeight: 500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 2,
              px: 1,
              backgroundColor: theme.palette.grey[100],
              userSelect: 'none',
              WebkitUserSelect: 'none',
              position: 'relative',
            }}
          >
            <Document
              file={PDF_URL}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <Typography color="text.secondary" py={4}>
                  Carregando documento…
                </Typography>
              }
              error={
                <Typography color="error" py={4}>
                  Não foi possível carregar o PDF. Verifique se o arquivo está disponível.
                </Typography>
              }
            >
              {Array.from(new Array(numPages), (_, index) => (
                <Box
                  key={`page_${index + 1}`}
                  ref={(el: HTMLDivElement | null) => {
                    pageRefs.current[index] = el
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={800}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    loading={
                      <Box sx={{ width: '100%', minHeight: 1100, bgcolor: 'grey.200', borderRadius: 1 }} />
                    }
                  />
                </Box>
              ))}
            </Document>

            {/* Botão flutuante para voltar ao Sumário */}
            {showBackToSummary && numPages > 0 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<VerticalAlignTopIcon />}
                onClick={handleBackToSummary}
                sx={{
                  position: 'sticky',
                  bottom: 24,
                  alignSelf: 'flex-start',
                  ml: 2,
                  zIndex: 10,
                  borderRadius: 6,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  boxShadow: 3,
                  opacity: 0.92,
                  '&:hover': { opacity: 1, boxShadow: 6 },
                }}
              >
                Voltar ao Sumário
              </Button>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
